import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  CircularProgress
} from '@mui/material';
import { 
  AccountBalance as AccountBalanceIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  SwapHoriz as SwapHorizIcon,
  CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import analyticsService from '../services/analyticsService';

const StatItem = ({ title, value, icon, color }) => (
  <Paper 
    elevation={2} 
    sx={{ 
      p: 2, 
      borderRadius: 2, 
      height: '100%',
      display: 'flex',
      alignItems: 'center'
    }}
  >
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        bgcolor: `${color}.light`, 
        color: `${color}.main`,
        borderRadius: '50%',
        p: 1.5,
        mr: 2
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

const DashboardSummary = ({ filters }) => {
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await analyticsService.getDashboardSummary(filters);
        setSummaryData(data);
        setError(null);
      } catch (err) {
        console.error('Error al obtener resumen del dashboard:', err);
        setError('No se pudo cargar el resumen del dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-PE', { 
      style: 'currency', 
      currency: 'PEN' 
    }).format(value);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!summaryData) {
    return null;
  }

  const { 
    total_transfers, 
    total_clients, 
    total_revenue, 
    total_amount_transferred, 
    avg_transfers_per_day,
    period
  } = summaryData;

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={2.4}>
          <StatItem 
            title="Total Transferencias" 
            value={total_transfers} 
            icon={<SwapHorizIcon />} 
            color="primary" 
          />
        </Grid>
        <Grid item xs={12} md={6} lg={2.4}>
          <StatItem 
            title="Total Clientes" 
            value={total_clients} 
            icon={<PeopleIcon />} 
            color="secondary" 
          />
        </Grid>
        <Grid item xs={12} md={6} lg={2.4}>
          <StatItem 
            title="Ingresos por Comisiones" 
            value={formatCurrency(total_revenue)} 
            icon={<TrendingUpIcon />} 
            color="success" 
          />
        </Grid>
        <Grid item xs={12} md={6} lg={2.4}>
          <StatItem 
            title="Monto Total Transferido" 
            value={formatCurrency(total_amount_transferred)} 
            icon={<AccountBalanceIcon />} 
            color="info" 
          />
        </Grid>
        <Grid item xs={12} md={6} lg={2.4}>
          <StatItem 
            title="Transferencias por Día" 
            value={avg_transfers_per_day} 
            icon={<CalendarTodayIcon />} 
            color="warning" 
          />
        </Grid>
      </Grid>
      
      {period.start_date && period.end_date && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Período: {period.start_date} a {period.end_date} ({period.days} días)
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DashboardSummary;
